import { ReactNode } from "react";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import {
  BulkButtonComponents,
  MemberTableSubject,
} from "@components/common/faro-table/faro-table-types";
import { MembersMobileViewItem } from "@pages/members/members-mobile-view-item";
import { SkeletonMemberListItemCard } from "@components/common/mobile/skeleton-member-list-item-card";
import { MembersSortBy, SdbMemberTypes } from "@custom-types/member-types";
import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { useAppSelector } from "@store/store-helper";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useSetMemberTableType } from "@hooks/table/use-set-member-table-type";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { SdbProject } from "@custom-types/project-types";

interface Props {
  /** List of members to display */
  members: SdbMemberTypes[];

  /** Whether the data is currently loading */
  isLoading: boolean;

  /** Bulk action buttons configuration */
  bulkActionButtons: BulkButtonComponents;

  /** Context of the table (company, team, group, etc.) */
  tableSubject: MemberTableSubject;

  /** Menu options to show for each member */
  menuItems: MenuItemOption<SdbMemberTypes>[];

  /** Optional footer content to render below bulk actions */
  renderFooterExtra?: () => ReactNode;

  /** Whether to show the footer extra content */
  shouldShowFooterExtra?: boolean;

  /** The selected group if there is one */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** The selected project if there is one */
  selectedProject?: SdbProject | null;

  /** Callback for loading more items (for pagination) */
  loadMoreItems?: () => Promise<void>;

  /** Current sort by value from the sort dropdown */
  sortBy: MembersSortBy | FaroCoreApiTypes.TeamMemberSortBy;
}

/**
 * Generic mobile list component for displaying members with virtualization,
 * selection, and bulk actions.
 *
 * Encapsulates the common pattern of VirtualizedGrid with MembersMobileViewItem
 * rendering and FaroTableBulkActions, with customization points for different contexts.
 */
export function MemberMobileList({
  members,
  isLoading,
  bulkActionButtons,
  tableSubject,
  menuItems,
  renderFooterExtra,
  shouldShowFooterExtra,
  group,
  selectedProject,
  sortBy,
  loadMoreItems,
}: Props): JSX.Element | null {
  useSetMemberTableType(tableSubject);

  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  const isAnyRowSelected = selectedEntities.length !== 0;
  const allMemberIds = members.map((member) => member.identity);

  if (!selectedSdbCompany) {
    return null;
  }

  return (
    <VirtualizedGrid
      // The `key` here forces React to remount the grid whenever `sortBy` changes.
      // This ensures that the scroll position resets and the grid re-renders from the first item
      key={sortBy}
      items={members}
      isLoading={isLoading}
      renderSkeletonItem={(index) => (
        <SkeletonMemberListItemCard key={`skeleton-${index}`} />
      )}
      estimateSize={78}
      renderItem={(member, index) => (
        <MembersMobileViewItem
          key={member.identity}
          member={member}
          index={index}
          companyId={selectedSdbCompany.id}
          tableSubject={tableSubject}
          menuItems={menuItems}
          group={group}
          selectedProject={selectedProject}
        />
      )}
      loadMoreItems={loadMoreItems}
      shouldRenderFooterOutside={isAnyRowSelected}
      renderFooterAction={() => (
        <>
          <FaroTableBulkActions
            bulkActionsButtons={bulkActionButtons}
            numberOfSelectedRows={selectedEntities.length}
            isLoading={isLoading}
            allRowIds={allMemberIds}
          />
          {!isAnyRowSelected &&
            shouldShowFooterExtra &&
            renderFooterExtra &&
            renderFooterExtra()}
        </>
      )}
    />
  );
}
